import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TagsInput from "@/components/ui/tags-input";
import {
  getCampaignFilterFieldsApiV1CampaignFilterFieldsGet,
  getCampaignFiltersApiV1CampaignFiltersGet,
  getCampaignFilterSuggestionsApiV1CampaignFilterSuggestionsGet,
  updateCampaignFiltersApiV1CampaignFiltersPut,
} from "@/backend-sdk/client/sdk.gen";
import { useToast } from "@/hooks/use-toast";
import { useGlobalFilter } from "@/features/filters/filter.context";

interface CampaignNameFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: "google" | "meta" | "ga4";
  customerUuid: string | null;
  accountId?: string;
  onSaved?: () => void;
}

type CampaignFilterField = string;

const CampaignNameFiltersModal: React.FC<CampaignNameFiltersModalProps> = ({
  open,
  onOpenChange,
  platform,
  customerUuid,
  accountId,
  onSaved,
}) => {
  const { toast } = useToast();
  const { getFilterQueryParams } = useGlobalFilter();
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [mode, setMode] = useState<"include" | "exclude">("include");
  const [field, setField] = useState<CampaignFilterField>("");
  const [includeTerms, setIncludeTerms] = useState<string[]>([]);
  const [excludeTerms, setExcludeTerms] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchRequestId = useRef(0);

  const fallbackSuggestions = useMemo(() => [], []);

  useEffect(() => {
    if (!open || !customerUuid) return;
    let mounted = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFieldsLoading(true);

    getCampaignFilterFieldsApiV1CampaignFilterFieldsGet({
      query: {
        customer_uuid: customerUuid,
        platform,
      },
      throwOnError: true,
    })
      .then((data) => {
        if (!mounted) return;
        setField(data.data[0]?.field ?? "");
        setSuggestions([]);
        setSearchTerm("");
      })
      .catch(() => {
        if (!mounted) return;
        toast({
          title: "Erro ao carregar campos",
          description: "Não foi possível buscar os campos disponíveis.",
          variant: "destructive",
        });
        setField("");
      })
      .finally(() => {
        if (!mounted) return;
        setFieldsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, customerUuid, platform, toast]);

  useEffect(() => {
    if (!open || !customerUuid || !field) return;
    let mounted = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);

    getCampaignFiltersApiV1CampaignFiltersGet({
      query: {
        customer_uuid: customerUuid,
        platform,
        field,
      },
      throwOnError: true,
    })
      .then((data) => {
        if (!mounted) return;
        setIncludeTerms(data.data.include || []);
        setExcludeTerms(data.data.exclude || []);
      })
      .catch(() => {
        if (!mounted) return;
        toast({
          title: "Erro ao carregar filtros",
          description: "Não foi possível buscar os filtros atuais.",
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, customerUuid, platform, field, toast]);

  const handleSave = async () => {
    if (!customerUuid) {
      toast({
        title: "Cliente não selecionado",
        description: "Selecione um cliente antes de salvar os filtros.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      await updateCampaignFiltersApiV1CampaignFiltersPut({
        query: {
          customer_uuid: customerUuid,
          platform,
          field,
        },
        body: {
          field,
          include: includeTerms,
          exclude: excludeTerms,
        },
        throwOnError: true,
      });
      toast({
        title: "Filtros salvos",
        description: "As campanhas serão filtradas nos relatórios.",
      });
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast({
        title: "Erro ao salvar filtros",
        description: "Verifique sua conexão e tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (value: string) => {
    setMode(value === "exclude" ? "exclude" : "include");
  };

  useEffect(() => {
    if (!open || !customerUuid || !field || !showSuggestions) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuggestions([]);
      return;
    }
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSuggestions([]);
      return;
    }

    const currentRequestId = ++searchRequestId.current;
    setSuggestionsLoading(true);

    const timer = setTimeout(() => {
      const filterParams = getFilterQueryParams();
      getCampaignFilterSuggestionsApiV1CampaignFilterSuggestionsGet({
        query: {
          customer_uuid: customerUuid,
          platform,
          field,
          search: trimmed,
          start_date: filterParams.start_date,
          end_date: filterParams.end_date,
          account_id: accountId,
        },
        throwOnError: true,
      })
        .then((data) => {
          if (searchRequestId.current !== currentRequestId) return;
          setSuggestions(data.data);
        })
        .catch(() => {
          if (searchRequestId.current !== currentRequestId) return;
          toast({
            title: "Erro ao buscar sugestões",
            description: "Não foi possível carregar sugestões de campanhas.",
            variant: "destructive",
          });
          setSuggestions([]);
        })
        .finally(() => {
          if (searchRequestId.current !== currentRequestId) return;
          setSuggestionsLoading(false);
        });
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [
    open,
    customerUuid,
    field,
    platform,
    searchTerm,
    showSuggestions,
    toast,
    fallbackSuggestions,
    getFilterQueryParams,
    accountId,
  ]);

  const applyTerms = (terms: string[]) => {
    if (mode === "include") {
      setIncludeTerms(terms);
    } else {
      setExcludeTerms(terms);
    }
  };

  const activeTerms = mode === "include" ? includeTerms : excludeTerms;

  const platformLabel =
    platform === "google"
      ? "Google Ads"
      : platform === "meta"
        ? "Meta Ads"
        : "Google Analytics (GA4)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Criar filtro</DialogTitle>
          <DialogDescription>
            Defina quais campanhas entrarão ou sairão dos relatórios de{" "}
            {platformLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-[220px_1fr]">
            <div className="space-y-2">
              <Label>Modo</Label>
              <Select value={mode} onValueChange={handleModeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="include">Incluir</SelectItem>
                  <SelectItem value="exclude">Excluir</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 pt-6">
              <Switch
                checked={showSuggestions}
                onCheckedChange={setShowSuggestions}
                id="show-suggestions"
              />
              <Label
                htmlFor="show-suggestions"
                className="text-xs leading-none"
              >
                Mostrar valores sugeridos ao digitar
              </Label>
            </div>
          </div>

          <TagsInput
            value={activeTerms}
            onChange={applyTerms}
            placeholder="Digite o termo da campanha..."
            suggestions={suggestions}
            showSuggestions={showSuggestions}
            disabled={!field || fieldsLoading}
            onQueryChange={setSearchTerm}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground">
                Incluindo
              </p>
              {includeTerms.length === 0 ? (
                <p className="text-sm">Nenhum termo.</p>
              ) : (
                <div className="mt-2 max-h-[210px] overflow-y-auto rounded-md border bg-background">
                  {includeTerms.map((term, index) => (
                    <div
                      key={`${term}-${index}`}
                      className={`px-3 py-2 text-sm ${index > 0 ? "border-t border-border/60" : ""}`}
                    >
                      {term}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground">
                Excluindo
              </p>
              {excludeTerms.length === 0 ? (
                <p className="text-sm">Nenhum termo.</p>
              ) : (
                <div className="mt-2 max-h-[210px] overflow-y-auto rounded-md border bg-background">
                  {excludeTerms.map((term, index) => (
                    <div
                      key={`${term}-${index}`}
                      className={`px-3 py-2 text-sm ${index > 0 ? "border-t border-border/60" : ""}`}
                    >
                      {term}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              saving || loading || fieldsLoading || !field || suggestionsLoading
            }
          >
            {saving ? "Salvando..." : "Salvar filtro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignNameFiltersModal;
